import React, { useState, useEffect } from "react";
import {
  Box,
  List,
  Avatar,
  Typography,
  useMediaQuery,
  Tooltip,
  Menu,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useClient } from "../context/ClientContext";
import LogoIcon from "./icons/logoIconDarkBg";
import { navItems } from "./mainMenu.utils";
import CollapsedNavItem from "./CollapsedNavItem";
import ExpandedNavItem from "./ExpandedNavItem";
import { useQuery } from "@apollo/client/react";
import { AUTH_COMPANIES_FOR_PARTNER_QUERY } from "../graphql/queries";
import { isPartnerCompanySwitchEnabled, isPartnerMultiTenantRole } from "../utils/partnerRoute";
import { useCompanySwitch } from "../hooks/useCompanySwitch";
import { ChevronDown } from "lucide-react";

type MainMenuProps = {
  onMobileClose?: () => void;
  isCollapsed?: boolean;
  onCollapseChange?: (collapsed: boolean) => void;
};

const MainMenu: React.FC<MainMenuProps> = ({ onMobileClose, isCollapsed: externalCollapsed, onCollapseChange }) => {
  const { user, login, logout } = useAuth();
  const { selectedClient, setClientFromLogin } = useClient();
  const location = useLocation();
  const navigate = useNavigate();
  const [openMenus, setOpenMenus] = useState<Set<string>>(new Set());
  const [internalCollapsed, setInternalCollapsed] = useState(false);
  const [dropdownAnchor, setDropdownAnchor] = useState<Record<string, HTMLElement | null>>({});
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const [companyMenuAnchor, setCompanyMenuAnchor] = useState<null | HTMLElement>(null);
  const { switchTo, switching, switchingCompanyId } = useCompanySwitch();
  const isMobile = useMediaQuery("(max-width:772px)");

  const isCollapsed = externalCollapsed !== undefined ? externalCollapsed : internalCollapsed;

  const handleCollapseToggle = () => {
    const newCollapsed = !isCollapsed;
    if (externalCollapsed === undefined) {
      setInternalCollapsed(newCollapsed);
    }
    if (onCollapseChange) {
      onCollapseChange(newCollapsed);
    }
  };

  const getFirstLetter = () => {
    if (user?.firstName) return user.firstName.charAt(0).toUpperCase();
    if (user?.userdisplayname || user?.name) {
      const name = (user.userdisplayname || user.name || "").trim();
      return name.charAt(0).toUpperCase() || "?";
    }
    if (user?.email) return user.email.charAt(0).toUpperCase();
    return "?";
  };

  const getFullName = () => {
    if (user?.firstName && user?.lastName) return `${user.firstName} ${user.lastName}`;
    return user?.userdisplayname || user?.name || "User";
  };

  const handleMenuItemClick = (route?: string | null) => {
    setUserMenuAnchor(null);
    if (route === null) logout();
    else if (route) navigate(route);
  };

  const [manuallyClosedMenus, setManuallyClosedMenus] = useState<Set<string>>(new Set());

  const toggleMenu = (menuText: string, hasActiveChild: boolean) => {
    const isCurrentlyOpen = isMenuOpen(menuText, hasActiveChild);
    if (isCurrentlyOpen) {
      if (hasActiveChild) {
        setManuallyClosedMenus((prev) => new Set(prev).add(menuText));
      }
      setOpenMenus((prev) => {
        const newSet = new Set(prev);
        newSet.delete(menuText);
        return newSet;
      });
    } else {
      setManuallyClosedMenus((prev) => {
        const newSet = new Set(prev);
        newSet.delete(menuText);
        return newSet;
      });
      setOpenMenus((prev) => new Set(prev).add(menuText));
    }
  };

  const isMenuOpen = (menuText: string, hasActiveChild: boolean) => {
    if (manuallyClosedMenus.has(menuText)) return false;
    if (openMenus.has(menuText)) return true;
    return hasActiveChild;
  };

  type AuthCompanyRow = {
    id: string;
    name: string;
    email?: string | null;
    subdomain?: string | null;
    role: string;
    parentCompanyId?: string | null;
    schemaName?: string | null;
    city?: string | null;
    state?: string | null;
    country?: string | null;
  };

  const partnerHomeCompanyId = (user as any)?.homeCompanyId || user?.companyId || null;
  const partnerCompanySwitchEnabled = isPartnerCompanySwitchEnabled();
  const isPartnerRole = partnerCompanySwitchEnabled && isPartnerMultiTenantRole(user?.role);

  const { data: partnerCompaniesData } = useQuery<{ companies: AuthCompanyRow[] }>(
    AUTH_COMPANIES_FOR_PARTNER_QUERY,
    {
      fetchPolicy: "cache-and-network",
      skip: !isPartnerRole,
    }
  );

  const selectablePartnerCompanies = (partnerCompaniesData?.companies || []).filter((c) => {
    const role = (c.role || "").toLowerCase();
    const schema = (c.schemaName || "").trim().toLowerCase();
    if (partnerHomeCompanyId && c.id === partnerHomeCompanyId) return false;
    if (role !== "company") return false;
    if (!schema || schema === "public") return false;
    return true;
  });

  const handlePartnerCompanySwitch = async (company: AuthCompanyRow) => {
    if (switching) return;
    const currentCompanyId = selectedClient?.id || user?.companyId;
    if (currentCompanyId && currentCompanyId === company.id) {
      setCompanyMenuAnchor(null);
      navigate("/dashboard");
      return;
    }
    await switchTo(
      { id: company.id, name: company.name },
      { beforeReload: () => setCompanyMenuAnchor(null) }
    );
  };

  // Auto-open menus when navigating to their children
  useEffect(() => {
    navItems.forEach((nav) => {
      if (nav.isParent) {
        const hasActiveChild = nav.children?.some((child) =>
          location.pathname.startsWith(child.path || "")
        ) ?? false;
        if (hasActiveChild) {
          setManuallyClosedMenus((prev) => {
            if (prev.has(nav.text)) {
              const newSet = new Set(prev);
              newSet.delete(nav.text);
              return newSet;
            }
            return prev;
          });
        }
      }
    });
  }, [location.pathname]);

  const handleDropdownAnchorChange = (key: string, el: HTMLElement | null) => {
    setDropdownAnchor((prev) => ({ ...prev, [key]: el }));
  };

  return (
    <Box display="flex" flexDirection="column" height="100vh" className="w-100">
      {/* Fixed Logo Section */}
      <Box
        sx={{
          px: isCollapsed ? 1 : '8px',
          pt: 0,
          pb: 0,
          display: "flex",
          justifyContent: isCollapsed ? "center" : "flex-start",
          alignItems: "center",
          flexShrink: 0,
          borderBottom: "1px solid #444",
        }}
      >
        {isCollapsed ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2, width: "100%" }}>
            <Tooltip title={selectedClient?.companyName} arrow placement="right">
              <Avatar sx={{ width: 32, height: 32, fontSize: 16, backgroundColor: "#2d3748" }}>
                {(selectedClient?.companyName || user?.userdisplayname || "Company").charAt(0).toUpperCase()}
              </Avatar>
            </Tooltip>
          </Box>
        ) : (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%", px: 0, py: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "40px", flexShrink: 0 }}>
              <Avatar sx={{ width: 30, height: 30, fontSize: 12, backgroundColor: "#2d3748" }}>
                {(selectedClient?.companyName || user?.userdisplayname || "Company").charAt(0).toUpperCase()}
              </Avatar>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                minWidth: 0,
                cursor: isPartnerRole ? "pointer" : "default",
              }}
              onClick={(e) => {
                if (!isPartnerRole) return;
                setCompanyMenuAnchor(e.currentTarget);
              }}
            >
              <Typography
                variant="h2"
                sx={{ color: "#fff", fontSize: "14px", fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
              >
                {selectedClient?.companyName || user?.userdisplayname || "Company"}
              </Typography>
              {isPartnerRole && <ChevronDown size={14} color="#9ca3af" />}
            </Box>
            <Menu
              anchorEl={companyMenuAnchor}
              open={Boolean(companyMenuAnchor)}
              onClose={() => setCompanyMenuAnchor(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
              PaperProps={{ sx: { minWidth: 260, maxHeight: 340 } }}
            >
              {selectablePartnerCompanies.length === 0 ? (
                <MenuItem disabled>No tenant companies available</MenuItem>
              ) : (
                selectablePartnerCompanies.map((company) => (
                  <MenuItem
                    key={company.id}
                    onClick={() => handlePartnerCompanySwitch(company)}
                    disabled={switching}
                    sx={{ display: "flex", justifyContent: "space-between", gap: 1.5 }}
                  >
                    <span>{company.name}</span>
                    {switchingCompanyId === company.id ? <CircularProgress size={16} /> : null}
                  </MenuItem>
                ))
              )}
              {/* <MenuItem onClick={() => { setCompanyMenuAnchor(null); navigate("/select-company"); }}>
                Open company selector page
              </MenuItem> */}
            </Menu>
          </Box>
        )}
      </Box>

      {/* Scrollable Navigation Items */}
      <Box
        flex={1}
        overflow="auto"
        py={1}
        className="side-nav-scrollable"
        sx={{
          "&::-webkit-scrollbar": { width: "3px", background: "transparent" },
          "&::-webkit-scrollbar-track": { background: "transparent", border: "none", margin: 0, padding: 0 },
          "&::-webkit-scrollbar-thumb": {
            background: "#666", borderRadius: "10px", border: "none", minHeight: "20px",
            "&:hover": { background: "#777" },
          },
          "&::-webkit-scrollbar-corner": { background: "transparent" },
          scrollbarWidth: "thin",
          scrollbarColor: "#666 transparent",
        }}
      >
        <List disablePadding>
          {navItems.map((nav) =>
            isCollapsed ? (
              <CollapsedNavItem
                key={nav.text}
                nav={nav}
                isMobile={isMobile}
                onMobileClose={onMobileClose}
                dropdownAnchor={dropdownAnchor}
                onDropdownAnchorChange={handleDropdownAnchorChange}
              />
            ) : (
              <ExpandedNavItem
                key={nav.text}
                nav={nav}
                isMobile={isMobile}
                onMobileClose={onMobileClose}
                isMenuOpen={isMenuOpen}
                toggleMenu={toggleMenu}
              />
            )
          )}
        </List>
      </Box>

      {/* Footer */}
      {isCollapsed ? (
        <Box px={1} pb={2} sx={{ mt: "auto", display: "flex", justifyContent: "center" }}>
          <LogoIcon width="32px" height="28px" />
        </Box>
      ) : (
        <Box
          borderTop="1px solid #444"
          sx={{ mt: "auto", p: 2, flexShrink: 0 }}
        >
          <Box className="">
            <img className="img-fluid m-auto" src='/images/logo-white.png' alt="Critical Asset" style={{ maxWidth: '150px' }} />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default MainMenu;
